import { useCallback, useContext } from "react";
import { toast } from "react-toastify";
import agent from "../API/apiCalls";
import { shouldShowErrorToast } from "../utils/apiRequestError";
import { useDashboardInfo } from "../Context/dashboardContext";
import {
  ApplicationEventHistory,
  EscalationResponse,
  FilterModel,
  GetApplicationListPayloadModel,
  GlobalResponseModel,
  IAddEscalationFuncParams,
  IApplicationInfo,
  IEscalationComments,
  IEscalationInfoRes,
  IGetAnalysisResultResponse,
  IMenuOption,
  IRaiseEscalationSuccessValue,
  SortInfoModel
} from "../Models/applicationModels";
import { InsightsDateRange } from "../Pages/InsightsPage/components/insightsHeader/insightsHeader.models";
import { CommonResponseModel, getApplicationData, getApplicationListRespModel, UnlockSsnDialogPayloadModel } from "../Models/tableDetailsModel";
import { UserContext, UserPayload } from "../Context/userContext";
import { useInsights } from "../Context/insightContext";
import GetTableColumns from "../Components/tableComponent/getTableColumn/getTableColumn";
import { columnsNotToShowInitially } from "../constants/InsightPageColumnsInfo";
import { useNavigate } from "react-router-dom";
import { rowCountPerPage } from "../constants/rowsPerPageOptions";
import { SendEmailPayload } from "../Components/CreateEscalation/createEscalation.models";
import { useLatestRequest } from "./useLatestRequest";
import { useEscalationInfo } from "../Context/escalationContext";
import { formatUTCDate, formatUTCDateTime } from "../utils/formatUTCDate";
import { GetTimeZoneName } from "../utils/getTimezoneOffset";
import { APPLICATION_DETAILS_DATE_KEYS, APPLICATION_DETAILS_DATE_ONLY_KEYS } from "../utils/constants/applicationDetailsBifurcation";

// A hook that includes multiple related API functions for insights
export const useInsightsUtils = () => {
  const { invalidateRequest, runLatest } = useLatestRequest();

  const {
    setFinalTechnicalOutcomeList,
    setApplicationListRows, 
  } = useDashboardInfo();

  const { setFlagUpdateEscalation } = useEscalationInfo();

  const {
    setTotalRowCount,
    visibleColumns,
    setVisibleColumns,
    setApplicationListColumns,
    setCurrentApplicationAnalysisResult,
    setSortInfo,
    setSearchText,
    setPage,
    totalRowCount,
    rowPerPageRef,
    searchTextRef,
    filterInfoRef,
    dateRangeRef,
    sortInfoRef,
    tableColumnInfo,
    tableWidth,
    tableBodyRef,
    searchText,
    rowsPerPage,
    setRowsPerPage,
    escalationInfo,
    setEscalationInfo,
    escalationDialogInfo,
    setEscalationDialogInfo,
    setDetailsPageEscalations,
    detailsPageOpportunityId,
    setAssociatedEscalation,
    isEventHistoryModalOpen,
    setIsEventHistoryModalOpen,
    isEventHistoryLoading,
    setIsEventHistoryLoading,
    eventHistoryResponse,
    setEventHistoryResponse,
    eventHistoryRequestData,
    setEventHistoryRequestData,
    setDetailsPageApplicationDetails
  } = useInsights();

  const { showLoader, setShowLoader } = useContext(UserContext) as UserPayload;

  const navigate = useNavigate();
  const GetApplicationList = async (
    size: number,
    pageNo: number,
    searchString?: string,
    sortItem?: SortInfoModel | null,
    filerInfo?: FilterModel[] | null
  ): Promise<getApplicationData | null> => {
    try {
      setShowLoader(true);

      let response: CommonResponseModel<getApplicationData> | null = null;
      const payload: GetApplicationListPayloadModel = {
        pageSize: size,
        pageIndex: pageNo,
        searchParam: searchString === "" ? null : searchString,
        sort: sortItem ?? null,
        filters: filerInfo ?? null,
        startDate: dateRangeRef.current.startDate,
        endDate: dateRangeRef.current.endDate,
        timezone: GetTimeZoneName()
      };
      response = await agent.GetApplicationListV1.post(payload);


      if (response?.flagStatus) {
        return response.value;
      } else {
        throw new Error("something went wrong! Please Try again later.");
      }

    } catch (e) {
      console.error(e);
      return null;
    } finally {
      setShowLoader(false);
    }
  };

  const GetAnalysisResultApi = async (opportunityId: string) => {
    try {
      setShowLoader(true);
      const response: IGetAnalysisResultResponse = await agent.GetAnalysisResult.get(opportunityId);
      setShowLoader(false);

      if (response.flagStatus && response.value) {
        setCurrentApplicationAnalysisResult({
          ...response.value,
          opportunityId
        })
      }
      else {
        throw new Error(JSON.stringify(response));
      }
    } catch (err) {
      setShowLoader(false);
      toast.error("Something went wrong, Please try again later!");
      console.error("Printing error", err);
    }
  };

  const HandleChangeSort = async (sortItem: SortInfoModel | null,) => {
    try {
      const currentSort = sortInfoRef.current;
      if (!currentSort || currentSort.value !== sortItem?.value || currentSort.sortDescending !== sortItem?.sortDescending) {
        const activeFilters = filterInfoRef.current;
        sortInfoRef.current = sortItem;
        const res = await GetApplicationList(rowPerPageRef.current, 1, searchTextRef.current, sortItem, activeFilters);
        if (res) {
          await UpdateTableInfo(res, sortItem);
          setPage(1);
          scrollToPosition({isScrollLeft: false});
          setSortInfo(sortItem);
        } else {
          sortInfoRef.current = currentSort;
          throw new Error("Something went wrong in the Sorting");
        }
      }
    } catch (e) {
      console.error("Error in HandleChangeSort: ", e);
    }
  };

  const HandleFilterChange = async (
    filterVal: FilterModel[],
    dateRange: InsightsDateRange
  ): Promise<boolean> => {
    try {
      const previousFilters = filterInfoRef.current;
      const previousDateRange = dateRangeRef.current;
      filterInfoRef.current = filterVal;
      dateRangeRef.current = dateRange;
      const activeSort = sortInfoRef.current;
      const res = await GetApplicationList(rowPerPageRef.current, 1, searchTextRef.current, activeSort, filterVal);
      if (res) {
        await UpdateTableInfo(res, activeSort);
        setPage(1);
        scrollToPosition({isScrollLeft: false});
        return true;
      } else {
        filterInfoRef.current = previousFilters;
        dateRangeRef.current = previousDateRange;
        throw new Error("Something went wrong in the Filtering");
      }
    } catch (e) {
      console.error("Error in HandleFilterChange: ", e);
      return false;
    }
  };

  const scrollToPosition = ({isScrollTop = true, isScrollLeft = true}: {isScrollTop?: boolean, isScrollLeft?: boolean}) => {
    const scroller = tableBodyRef.current?.parentElement?.parentElement;

    if (!scroller) return;

    scroller.scroll({
      top: isScrollTop ? 0 : scroller.scrollTop,
      left: isScrollLeft ? 0 : scroller.scrollLeft,
      behavior: 'smooth',
    });
  };

  const OnSearchHandler = async (value: string) => {
    try {
      if (searchTextRef.current === value || showLoader) {
        return;
      }
      const res = await GetApplicationList(rowsPerPage, 1, value, sortInfoRef.current, filterInfoRef.current);
      if (res) {
        searchTextRef.current = value;
        setSearchText(value);
        await UpdateTableInfo(res, sortInfoRef.current);
        setPage(1);
        scrollToPosition({});
      } else {
        throw new Error("Something went wrong in the Searching");
      }

    } catch (e) {
      console.error(e);
    }
  };

  const HandleChangePage = async (rowsPerPage: number, newPage: any) => {
    try {
      const res = await GetApplicationList(rowsPerPage, newPage, searchText, sortInfoRef.current, filterInfoRef.current);
      if (res) {
        await UpdateTableInfo(res, sortInfoRef.current);
        scrollToPosition({ isScrollLeft: false });
      } else {
        throw new Error("Something went wrong in changing page.");
      }
      setPage(newPage);
    } catch (e) {
      console.error(e);
    }
  };

  const HandleRowPerPageChange = async (oldRowPerPage: number, newRowPerPage: number, pageNo: number) => {
    const currentFirstRecordIndex = (pageNo - 1) * oldRowPerPage + 1;
    if (currentFirstRecordIndex > totalRowCount) {
      throw new Error("Invalid Page Number");
    }

    const newPage = Math.ceil(currentFirstRecordIndex / newRowPerPage);
    try {
      const res = await GetApplicationList(newRowPerPage, newPage, searchText, sortInfoRef.current, filterInfoRef.current);
      if (res) {
        await UpdateTableInfo(res, sortInfoRef.current);
        scrollToPosition({isScrollLeft: false});
      } else {
        throw new Error("Something went wrong in changing page.");
      }
      setPage(newPage);
      setRowsPerPage(newRowPerPage as rowCountPerPage);
    } catch (e) {
      console.error(e);
    }
  };

  const HandleMoreInfoNavigate = (opportunityId: string) => {
    navigate("/insights/moreInfo?opportunityId=" + opportunityId)
  }

  const HandleCloseEventHistoryModal = () => {
    invalidateRequest("eventHistory");
    setIsEventHistoryModalOpen(false);
    setEventHistoryRequestData(null);
    setEventHistoryResponse(null);
    setIsEventHistoryLoading(false);
  } 

  const HandleLastEventClick = async (opportunityUid: string, firstName: string, lastName: string) => {
    if (!opportunityUid) return; 
    
    const applicantName = [firstName, lastName].filter(Boolean).join(" ").trim() || null;
    setEventHistoryRequestData({
      opportunityUid,
      firstName: firstName ?? "",
      lastName: lastName ?? ""
    });
    setIsEventHistoryModalOpen(true);
    setIsEventHistoryLoading(true);
    setEventHistoryResponse(null);

    await runLatest("eventHistory", {
      request: () => agent.GetEventHistory.get(opportunityUid),
      onSuccess: (response: GlobalResponseModel<ApplicationEventHistory>) => {
        if (response.flagStatus && response.value) {
          setEventHistoryResponse({
            application: applicantName,
            eventsResponse: response.value
          });
          return;
        }
        throw new Error("Error in getting event history");
      },
      onError: (error) => {
        console.error("Error in HandleLastEventClick:", error);
        if (shouldShowErrorToast(error)) {
          toast.error("Unable to fetch event history. Please try again.");
        }
      },
      onFinally: () => {
        setIsEventHistoryLoading(false);
      }
    });
  }

  const HandleRetryEventHistory = async () => {
    if (!eventHistoryRequestData?.opportunityUid) {
      toast.error("Unable to retry event history. Opportunity ID not found.");
      return;
    }

    await HandleLastEventClick(
      eventHistoryRequestData.opportunityUid,
      eventHistoryRequestData.firstName,
      eventHistoryRequestData.lastName
    );
  }

  const TransferEscalationData = (escalationsData: IEscalationInfoRes[]) => {
    return escalationsData.map(({ escalationUid, escalationName }) => ({
      id: escalationUid,
      name: escalationName,
    }))
  }

  const UpdateRowData = (
    rowData: getApplicationListRespModel[],
    flagSingleUpdate = false,
    oppId: string = "",
    singleData?: getApplicationListRespModel
  ) => {
    return rowData.map((v) => {
      if (flagSingleUpdate && v.opportunityUid === oppId && singleData) {
        v = { ...singleData }; // Replace the matching row
      }

      const updatedRow = { ...v };

      updatedRow.agentName = [v.userFirstName, v.userLastName].filter(Boolean).join(" ").trim() || null;
      updatedRow.applicantName = [v.firstName, v.middleName, v.lastName].filter(Boolean).join(" ").trim() || null;

      for(const field of APPLICATION_DETAILS_DATE_ONLY_KEYS) {
        const value = v[field];
        if (typeof value === "string") {
          updatedRow[field] = formatUTCDate({ dateString: value, dateMonthYear: true});
        }
      }

      for(const field of APPLICATION_DETAILS_DATE_KEYS) {
        const value = v[field];
        if (typeof value === "string") {
          updatedRow[field] = formatUTCDateTime(value, false, true);
        }
      }

      return updatedRow;
    });
  };

  const UpdateTableInfo = async (
    applicationInfo: getApplicationData,
    sortItem: SortInfoModel | null,
    setAvailableEscalations?: React.Dispatch<React.SetStateAction<IEscalationInfoRes[]>>
  ) => {

    const customRows = UpdateRowData(applicationInfo.applicationRes);
    setApplicationListRows(customRows);
    setTotalRowCount(applicationInfo.applicationCount);

    if (escalationInfo?.length === 0) {
      // Set escalation map only if it is not set.
      setEscalationInfo(applicationInfo.availableEscalations);
    }

    const noData = customRows.length === 0;
    if (!noData || tableColumnInfo.current !== null) {
      // Store First row info to use When not get table info.
      if (tableColumnInfo.current === null) {
        tableColumnInfo.current = customRows[0];
      }
      const firstRowInfo = noData ? tableColumnInfo.current : customRows[0];

      const menuOptions: IMenuOption = {
        escalation: TransferEscalationData(applicationInfo.availableEscalations),
      }
      if (setAvailableEscalations) {
        setAvailableEscalations(applicationInfo.availableEscalations);
      }

      const columns = GetTableColumns({
        rowData: firstRowInfo,
        sortInfo: sortItem ?? null,
        handleChangeSort: HandleChangeSort,
        GetAnalysisResultApi,
        tableWidth: tableWidth,
        handleNavigate: HandleMoreInfoNavigate,
        menuOption: menuOptions,
        HandleAddEscalation,
        handleLastEventClick: HandleLastEventClick
      });
      // Fill data in the visibleState.
      if (visibleColumns === null) {
        setVisibleColumns((prev) => {
          if (prev === null) {
            return columns
              .filter((column) => !columnsNotToShowInitially.includes(column.key))
              .map((v) => v.key);
          }
          return prev;
        })
      }
      setApplicationListColumns(columns);
    } else {
      if (tableBodyRef.current) {
        const parentElement = tableBodyRef.current?.parentElement?.parentElement;
        if (parentElement) {
          parentElement.style.minHeight = 'calc(100vh - 280px)';
        }
      }
    }
  };

  // Fetch final GetFinalTechnicalOutcomeListApi outcome list
  const GetFinalTechnicalOutcomeList = useCallback(async () => {
    try {
      const response = await agent.GetFinalTechnicalOutcomeList.get();
      console.log("Printing response of get final technical outcome", response);
      if (response.flagStatus && response.value && response.value.length > 0) {
        setFinalTechnicalOutcomeList(response.value);
      }
    } catch (err) {
      toast.error("Something went wrong, Please try again later!");
      console.error("Printing error", err);
    }
  }, [setFinalTechnicalOutcomeList]);


  const GetUpdateRowData = async (oppId: string): Promise<IApplicationInfo | null> => {
    try {
      setShowLoader(true);

      const res: CommonResponseModel<IApplicationInfo> = await agent.GetApplicationInfoByOpportunityId.get(oppId);

      if (!res.flagStatus || res.isAxiosError || res.error) {
        throw new Error("Error in getting updated row data");
      }

      return res.value;

    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setShowLoader(false);
    }
  }



  const UpdateOppIdRowData = async (oppId: string) => {
    try {
      setShowLoader(true);
      const res = await GetUpdateRowData(oppId);

      if (res === null) {
        throw new Error("Error in Updating application data");
      }

      setApplicationListRows((prev) => {
        if (!prev) return prev;

        const [formattedSingleRow] = UpdateRowData([res.application]);

        return prev.map((row) =>
          row.opportunityUid === oppId ? formattedSingleRow : row
        );
      })

      if (detailsPageOpportunityId === oppId) {
        setDetailsPageEscalations(res.escalations);
        const appEscalations = res.application?.associatedEscalations;
        setAssociatedEscalation(Array.isArray(appEscalations) ? (appEscalations) : []);
        setCurrentApplicationAnalysisResult(res.analysis);
        setDetailsPageApplicationDetails(res.application);
      }
    } catch (err) {
      console.log("Error in UpdateOppIdRowData:", err);
    } finally {
      setShowLoader(false);
    }
  }

  const HandleAddEscalation = async ({ oppId, escalationId, escalationName, agencyName, applicationEscalationUid = undefined, handleCloseEscalationPopUp }: IAddEscalationFuncParams) => {
    try {
      setShowLoader(true);

      const response: GlobalResponseModel<EscalationResponse> = await agent.GetEscalation.get(oppId, escalationId, applicationEscalationUid);

      if (response.error || !response.flagStatus || response.value === null) {
        throw new Error("Error in getting escalation info");
      }

      const dialogBoxInfo = {
        oppId,
        escalationName,
        agencyName,
        escalationId,
        handleCloseEscalationPopUp,
        ...response.value,
      };
      setEscalationDialogInfo(dialogBoxInfo);
      setFlagUpdateEscalation(false);

    } catch (error) {
      console.log("Error in HandleAddEscalation:", error);
    } finally {
      setShowLoader(false);
    }
  }

  const ResetEscalationDialogBoxData = async (flagUpdateData = false, oppId: string) => {
    if (flagUpdateData) {
      if (!oppId) {
        console.log("Error in ResetEscalationDialogBoxData: oppId not found");
        return;
      }
      await UpdateOppIdRowData(oppId);
    }
    setEscalationDialogInfo(null);
  }

  const SaveEscalationData = async (oppId: string, escalationUid: string, payload: FormData, flagPutCall: boolean) => {
    try {
      setShowLoader(true);
      const response: CommonResponseModel<IRaiseEscalationSuccessValue> =
        !flagPutCall ? (await agent.RaiseEscalationPost.postForm(oppId, escalationUid, payload)) :
          (await agent.RaiseEscalationPut.putForm(oppId, escalationUid, payload));


      if (response.isAxiosError || !response.flagStatus || response.value === null) {
        throw new Error("Error in saving escalation data");
      }

      const value = response.value;
      if (!value) {
        return;
      }

      if (response.flagStatus) {
        setApplicationListRows((prev) =>
          prev
            ? prev.map((row) =>
                row.opportunityUid === oppId
                  ? { ...row, associatedEscalations: value.associatedEscalations ?? [] }
                  : row
              )
            : prev
        );
        if (detailsPageOpportunityId === oppId) {
          if (value.escalations) {
            setDetailsPageEscalations(value.escalations);
          }
          if (value.associatedEscalations) {
            setAssociatedEscalation(value.associatedEscalations);
          }
        }
        const shouldRefreshEscalatedPageData = payload.get(SendEmailPayload.FLAG_SEND_MAIL) === "true";
        const closeEscalationDialog = escalationDialogInfo?.handleCloseEscalationPopUp ?? ResetEscalationDialogBoxData;
        await closeEscalationDialog(shouldRefreshEscalatedPageData, oppId);
      }

      scrollToPosition({isScrollLeft: false});
      const message = payload.get(SendEmailPayload.FLAG_SEND_MAIL)
        ? "Escalation raised successfully and mail sent!"
        : "Escalation raised successfully!";
      toast.success(message);
    } catch (e) {
      console.error("Error in SaveEscalationData:", e);
    } finally {
      setShowLoader(false);
    }
  }

  const UpdateEscalationStatus = async (applicationEscalationUid: string, optionId: string, formData: FormData, setCommentItems: React.Dispatch<React.SetStateAction<IEscalationComments[]>>) => {
    try {
      setShowLoader(true);
      const response: GlobalResponseModel<EscalationResponse> = await agent.UpdateEscalationStatus.putForm(applicationEscalationUid, optionId, formData);

      if (!response.flagStatus || response.error || response.value === null) {
        throw new Error("Error in updating escalation status");
      }

      setEscalationDialogInfo(prv => prv ? { ...prv, ...response.value } : null);
      setFlagUpdateEscalation(false);
      setCommentItems(response.value?.escalationComments ?? []);
      return true;
    } catch (e) {
      console.error("Error in SaveEscalationData:", e);
      return false;
    } finally {
      setShowLoader(false);
    }
  }

  const UnLockApplication = async (oppId: string) => {
    try {
      setShowLoader(true);
      const response: GlobalResponseModel<boolean> = await agent.UnlockApplication.get(oppId);
      if(response.value){
        await UpdateOppIdRowData(oppId);
      }
    }catch (e) {
      console.error("Error in UnLockApplication:", e);
    }finally {
      setShowLoader(false);
    }
  }

  const UnLockSsnApplication = async (payload: UnlockSsnDialogPayloadModel) => {
    try {
      setShowLoader(true);
      const response: GlobalResponseModel<boolean> = await agent.UnlockSsnApplication.post(payload);
      if(response?.flagStatus && response?.value){
        toast.success("Identity Unlocked Successfully!");
        await UpdateOppIdRowData(payload.opportunityUid);
      } else {
        throw new Error("Error in unlocking Identity");
      }
    } catch (e) {
      console.error("Error in UnLocking Identity Application:", e);
    } finally {
      setShowLoader(false);
    }
  }

  return {
    GetFinalTechnicalOutcomeList,
    GetApplicationList,
    GetAnalysisResultApi,
    HandleChangeSort,
    HandleFilterChange,
    UpdateTableInfo,
    HandleMoreInfoNavigate,
    OnSearchHandler,
    HandleChangePage,
    HandleRowPerPageChange,
    SaveEscalationData,
    ResetEscalationDialogBoxData,
    UpdateEscalationStatus,
    HandleAddEscalation,
    UnLockApplication,
    UnLockSsnApplication,
    TransferEscalationData,
    HandleCloseEventHistoryModal,
    HandleLastEventClick,
    HandleRetryEventHistory,
    isEventHistoryModalOpen,
    isEventHistoryLoading,
    eventHistoryResponse
  };
};
